import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { OrigamiResponse, ServiceBase } from '@app/origami-api/services/base/service.base';
import { IrbTopic, SimpleTopic, TmfPublishing, TopicField } from '@app/origami-api/utils/topic-utils';
import { InstitutionalReviewBoardFilterMode } from '@app/origami-api/constants/institutional-review-board-filter-mode';
import { ErrorRecord } from '@app/origami-api/utils/error-utils';
import { IrbEcModel } from '@app/pages/main/pages/startup-irb-ec/models/interfaces/irb-ec.interfaces';
import { IrbType } from '@app/origami-api/constants/irb-types';
import { IEntityIrbTopics } from '@app/pages/main/pages/startup-sites/components/sites-modals/create-site-modal/popups/add-existing-irb/add-existing-irb.component';

export namespace InstitutionalReviewBoardServiceOptions {
  import InstitutionalReviewBoardStatus = IrbEcModel.InstitutionalReviewBoardStatus;

  export interface GetInstitutionalReviewBoards {
    siteId: number;
    amendmentProfileId?: number;
    investigativeSiteId?: number;
    filterMode?: InstitutionalReviewBoardFilterMode;
    viewMode?: number;
    searchClause?: string | number;
    searchQuery?: string;
    tmfPublishing?: TmfPublishing;
    orderBy?: string;
    limit?: number;
  }

  export interface GetInstitutionalReviewBoard {
    siteId: number;
    institutionalReviewBoardTopicId: number;
  }

  export interface LinkInstitutionalReviewBoard {
    siteId: number;
    investigativeSiteId: number;
    irbTopics: SimpleTopic[];
  }

  export interface UnlinkInstitutionalReviewBoard {
    siteId: number;
    investigativeSiteId: number;
    irbTopics: SimpleTopic[];
  }

  export interface GetInstitutionalReviewBoardViews {
    siteId: number;
    viewMode: GetInstitutionalReviewBoardViewsVModes;
    amendmentProfileId?: number;
    viewFilter: string | number;
  }

  export enum GetInstitutionalReviewBoardViewsVModes {
    ByStatus = 1,
    ByCountry = 2,
    ByInvSite = 3,
  }

  export interface ModifyInstitutionalReviewBoard {
    institutionalReviewBoard: IrbTopic;
  }

  export interface CreateInstitutionalReviewBoard {
    institutionalReviewBoard: IrbTopic;
    investigativeSiteId?: number;
  }

  export interface ChangeInstitutionalReviewBoardStatus {
    siteId: number;
    irbId: number;
    irbFields: TopicField[];
    status: InstitutionalReviewBoardStatus;
    documentIdsMovingToEtmf: number[];
    completeAmendments: boolean;
    doNotMoveAnyDocumentsToEtmf: boolean;
  }

  export interface ChangeInstitutionalReviewBoardInvSiteTypeParams {
    siteId: number;
    investigativeSiteDomainTopicId: number;
    irbDomainTopicId: number;
    irbInInvSiteType: IrbType;
    invSiteFieldsToUpdate?: TopicField[];
  }
}

export namespace InstitutionalReviewBoardServiceModel {
  export enum IRBInInvestigativeSiteType {
    Central = 'Central',
    Local = 'Local',
  }

  export interface GetInstitutionalReviewBoardsResult {
    InstitutionalReviewBoards: IrbTopic[];
    InvestigativeSiteAccessRight?: number;
    TotalRowCount: number;
  }

  export interface LinkInstitutionalReviewBoardsResult {
    ErrorRecords: ErrorRecord[];
    JobIds: string[];
  }

  export interface UnlinkInstitutionalReviewBoardsResult {
    ErrorRecords: ErrorRecord[];
    JobIds: string[];
  }

  export enum IrbType {
    Central = 1,
    Local = 2,
  }

  export interface ModifyInstitutionalReviewBoardsResult {
    ErrorCode: number;
    Result: IrbTopic[];
  }

  export interface CreateInstitutionalReviewBoardsResult {
    ErrorCode: number;
    Result: LinkInstitutionalReviewBoardsResult;
    JobIds: number | string;
    EntityTopics: IEntityIrbTopics[];
  }
}

@Injectable()
export class InstitutionalReviewBoardService extends ServiceBase {
  serviceUrl = 'InstitutionalReviewBoardService.svc';

  getInstitutionalReviewBoards(
    body: InstitutionalReviewBoardServiceOptions.GetInstitutionalReviewBoards,
    ...params
  ): Observable<OrigamiResponse<InstitutionalReviewBoardServiceModel.GetInstitutionalReviewBoardsResult>> {
    return this.origamiPost('/GetInstitutionalReviewBoards', body, ...params);
  }

  getInstitutionalReviewBoard(
    body: InstitutionalReviewBoardServiceOptions.GetInstitutionalReviewBoard,
    ...params
  ): Observable<OrigamiResponse<IrbTopic>> {
    return this.origamiPost('/GetInstitutionalReviewBoard', body, ...params);
  }

  linkInstitutionalReviewBoard(
    body: InstitutionalReviewBoardServiceOptions.LinkInstitutionalReviewBoard,
    ...params
  ): Observable<OrigamiResponse<InstitutionalReviewBoardServiceModel.LinkInstitutionalReviewBoardsResult>> {
    return this.origamiPost('/LinkInstitutionalReviewBoard', body, ...params);
  }

  unlinkInstitutionalReviewBoard(
    body: InstitutionalReviewBoardServiceOptions.UnlinkInstitutionalReviewBoard,
    ...params
  ): Observable<OrigamiResponse<InstitutionalReviewBoardServiceModel.UnlinkInstitutionalReviewBoardsResult>> {
    return this.origamiPost('/UnlinkInstitutionalReviewBoard', body, null, null, true);
  }

  getInstitutionalReviewBoardViews(
    body: InstitutionalReviewBoardServiceOptions.GetInstitutionalReviewBoardViews,
    ...params
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/GetInstitutionalReviewBoardViews', body, ...params);
  }

  modifyInstitutionalReviewBoard(
    body: InstitutionalReviewBoardServiceOptions.ModifyInstitutionalReviewBoard,
    ...params
  ): Observable<OrigamiResponse<InstitutionalReviewBoardServiceModel.ModifyInstitutionalReviewBoardsResult>> {
    return this.origamiPost('/ModifyInstitutionalReviewBoard', body, ...params);
  }

  createInstitutionalReviewBoard(
    body: InstitutionalReviewBoardServiceOptions.CreateInstitutionalReviewBoard,
    ...params
  ): Observable<OrigamiResponse<InstitutionalReviewBoardServiceModel.CreateInstitutionalReviewBoardsResult>> {
    return this.origamiPost('/CreateInstitutionalReviewBoard', body, ...params);
  }

  changeInstitutionalReviewBoardStatus(
    body: InstitutionalReviewBoardServiceOptions.ChangeInstitutionalReviewBoardStatus,
    ...params
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/ChangeInstitutionalReviewBoardStatus', body, ...params);
  }

  changeInstitutionalReviewBoardInvSiteType(
    body: InstitutionalReviewBoardServiceOptions.ChangeInstitutionalReviewBoardInvSiteTypeParams,
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/ChangeInstitutionalReviewBoardInvSiteType', body, null, null, true);
  }
}
